/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { CheckmarkOutline } from '@carbon/react/icons';
import { Loading, Modal, Link } from 'carbon-components-react';
import React from 'react';

import RocketImg from '@ibm-watson/shared/assets/images/rocket_iso.svg';
import { Noop } from '../../../../ts';

const strings = {
  MODAL_LABEL: 'Upgrade',
  MODAL_HEADING: 'Launch Plus plan',
  FEATURES_HEADER: 'With the Plus plan, you can:',
  PLUS_FEATURE_1: 'Get up to 50 skills per instance',
  PLUS_FEATURE_2:
    "Answer your customer's questions in real-time via phone, SMS and web",
  PLUS_FEATURE_3:
    'Train faster with intent recommendations which proposes new intents and highlights conflicts in your data',
  PLUS_FEATURE_4:
    "Use Search Skills to provide answers to your users' questions without the need for you to create explicit dialog",
  FEATURES_FOOTER:
    'Would you like to continue upgrading to Plus, starting at $140 for 1,000 users? For more information on tracking users and user-based billing visit our docs.',
  LEARN_MORE: 'Learn more',
  AGREE: 'Agree',
  CANCEL: 'Cancel'
};

const docLink =
  'https://cloud.ibm.com/docs/assistant?topic=assistant-services-information#services-information-user-based-plans';

export interface PlusConfirmationModalProps {
  upgradingPlan: boolean;
  onRequestSubmit: Noop;
  onSecondarySubmit: Noop;
  onRequestClose: Noop;
}

const PlusConfirmationModal: React.FC<PlusConfirmationModalProps> = ({
  upgradingPlan,
  onRequestSubmit,
  onSecondarySubmit,
  onRequestClose
}) => {
  const featureList = [
    'PLUS_FEATURE_1',
    'PLUS_FEATURE_2',
    'PLUS_FEATURE_3',
    'PLUS_FEATURE_4'
  ];

  return (
    <Modal
      open
      className="PlusConfirmationModal"
      modalLabel={strings.MODAL_LABEL}
      modalHeading={strings.MODAL_HEADING}
      preventCloseOnClickOutside
      primaryButtonText={strings.AGREE}
      secondaryButtonText={strings.CANCEL}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      onSecondarySubmit={onSecondarySubmit}>
      <Loading active={upgradingPlan} withOverlay={true} />
      <div className="UpgradeModal__body">
        <div>
          <p className="UpgradeModal__text">{strings.FEATURES_HEADER}</p>
          <ul className="UpgradeModal__list">
            {featureList.map(item => (
              <li key={item}>
                <CheckmarkOutline size={16} />
                {strings[item]}
              </li>
            ))}
          </ul>
          <p className="UpgradeModal__text">
            {strings.FEATURES_FOOTER}
            <Link target="_blank" rel="noopener noreferrer" href={docLink}>
              {strings.LEARN_MORE}
            </Link>
          </p>
        </div>
        <div className="UpgradeModal__image">
          <img src={RocketImg} alt="" />
        </div>
      </div>
    </Modal>
  );
};

export default PlusConfirmationModal;
